// Cave84 — Tomer Gal invoice parser (pure; no React, no Firebase).
//
// Tomer Gal's invoices are issued by B.W.C. Import (1998) Ltd (בי.דאבליו.סי
// יבוא, VAT no. 512679788) from the Rivhit accounting system: a Hebrew, RTL,
// one-table "חשבונית מס" in ILS with prices INCLUDING 18% VAT.
//
// pdfjs text, after pdfItemsToPageText (rows top→bottom, items left→right),
// puts each table row in reverse column order:
//
//   360.00 180.00 2.00 Chateau des Quarts - Pouilly-Fuisse 1er cru 61002 1
//   l'Orangerie 2022                          ← description wrapped onto a 2nd row
//   2,600.00 2,600.00 1.00 השתתפות בטעימת יין 16.10 99900 6
//   line total · unit price · qty · description · item no. · line #
//
// Wine descriptions are "Producer - Appellation [Climat] Vintage". Lines with a
// Hebrew-only description (a tasting, delivery) are not wine: they are listed
// as excluded — never dropped silently — and still counted in the totals check.

import type { BottleFormat } from '@cave84/shared-types';
import { inferAppellation } from '@cave84/shared-types';
import { inferStyle, mk, round2, NV_YEAR } from './mwcInvoiceParser';
import type { ReconCheck } from './mwcInvoiceParser';
import type { ExcludedLine, ImportLine, SupplierInvoice } from './invoiceImport';

const AMOUNT = String.raw`-?\d{1,3}(?:,\d{3})*\.\d{2}`;

// line total · unit price · qty · description · item no. · line #
const LINE_RE = new RegExp(
  String.raw`^(${AMOUNT})\s+(${AMOUNT})\s+(${AMOUNT})\s+(.+?)\s+([A-Za-z0-9-]{2,15})\s+(\d{1,3})$`,
);

// Hebrew labels. Quotes may be ASCII " or Hebrew gershayim ״.
const Q = `["״]`;
const RE_TOTAL_INCL   = new RegExp(`סה${Q}כ כולל מע${Q}מ\\s*:`);
const RE_TOTAL_TO_PAY = new RegExp(`סה${Q}כ לתשלום`);
const RE_NET          = new RegExp(`סה${Q}כ ללא מע${Q}מ`);
const RE_VAT_ROW      = new RegExp(`(\\d+(?:\\.\\d+)?)\\s*%\\s*מע${Q}מ|מע${Q}מ\\s*(\\d+(?:\\.\\d+)?)\\s*%`);
const RE_TABLE_HEADER = /תיאור פריט/;
const RE_SUPPLIER     = /BWC|בי\.?דאבליו\.?סי|512679788/;
// Rows that are page furniture, never part of a wrapped description.
const RE_BOILERPLATE  = new RegExp(
  `דף \\d+ מתוך|מסמך זה הופק|rivhit|מס' פריט|לכבוד|טלפון|מספר|תאריך|העברה|סניף|חשבון|מוטב|עוסק מורשה|בע${Q}מ|המשך|הנחה|סה${Q}כ`,
  'i',
);

const HAS_LATIN = /[A-Za-z]/;

function amount(s: string): number {
  return parseFloat(s.replace(/,/g, ''));
}

/** Amounts in a row, left→right, ignoring percentages ("18.00%"). */
function amountsIn(row: string): number[] {
  return [...row.replace(/\d+(?:\.\d+)?\s*%/g, ' ').matchAll(new RegExp(AMOUNT, 'g'))].map(m => amount(m[0]));
}

// Local importers abbreviate the Côte d'Or communes ("Leflaive - Puligny 1er cru
// Clavoillon"). Expanding them keeps the catalog's cuvée names uniform and lets
// the shorthand match a wine stored under the full name.
const COMMUNES: [RegExp, string][] = [
  [/\bPuligny(?:[ -]Montrachet)?\b/gi, 'Puligny-Montrachet'],
  [/\bChassagne(?:[ -]Montrachet)?\b/gi, 'Chassagne-Montrachet'],
  [/\bGevrey(?:[ -]Chambertin)?\b/gi, 'Gevrey-Chambertin'],
  [/\bChambolle(?:[ -]Musigny)?\b/gi, 'Chambolle-Musigny'],
  [/\bVosne(?:[ -]Roman[ée]e)?\b/gi, 'Vosne-Romanée'],
  [/\bMorey(?:[ -](?:St\.?|Saint)[ -]Denis)?\b/gi, 'Morey-Saint-Denis'],
  [/\bNuits(?:[ -](?:St\.?|Saint)[ -]Georges)?\b/gi, 'Nuits-Saint-Georges'],
];

function tidyCuvee(s: string): string {
  let out = s;
  for (const [rx, full] of COMMUNES) out = out.replace(rx, full);
  return out
    .replace(/\b1er cru\b/gi, '1er Cru')
    .replace(/\bgrand cru\b/gi, 'Grand Cru')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

const FORMATS: [RegExp, BottleFormat][] = [
  [/\b(?:double[ -]?magnum|3(?:\.0)?\s?l(?:t|tr)?|300\s?cl)\b/i, 'double_magnum'],
  [/\b(?:magnum|1\.5\s?l(?:t|tr)?|150\s?cl)\b/i, 'magnum'],
  [/\b(?:half(?:[ -]bottle)?|0?\.375\s?l(?:t|tr)?|375\s?ml|37\.5\s?cl)\b/i, 'half'],
];

/**
 * "Chateau des Quarts - Pouilly-Fuisse 1er cru l'Orangerie 2022"
 *   → { producer: "Chateau des Quarts", year: 2022, cuvee: "Pouilly-Fuisse 1er Cru l'Orangerie" }
 * null when there is no " - " producer separator or no vintage / NV.
 */
export function parseTomerGalWine(desc: string):
  { producer: string; year: number; cuvee: string; format: BottleFormat } | null {
  const sep = /\s+[-–]\s+/.exec(desc);
  if (!sep) return null;
  const producer = desc.slice(0, sep.index).trim();
  let rest = desc.slice(sep.index + sep[0].length).trim();

  let format: BottleFormat = '750ml';
  for (const [rx, f] of FORMATS) {
    if (rx.test(rest)) { format = f; rest = rest.replace(rx, ' '); break; }
  }

  let year: number | null = null;
  const years = [...rest.matchAll(/\b(19\d{2}|20\d{2})\b/g)];
  const last = years[years.length - 1];
  if (last) {
    year = parseInt(last[1]!, 10);
    rest = rest.slice(0, last.index) + rest.slice(last.index! + last[0].length);
  } else if (/\bN\.?V\.?\b/i.test(rest)) {
    year = NV_YEAR;
    rest = rest.replace(/\bN\.?V\.?\b/i, ' ');
  }
  if (!producer || year == null) return null;
  return { producer, year, cuvee: tidyCuvee(rest.replace(/[,\s]+$/, '')), format };
}

interface RawLine {
  no: number;
  item: string;
  qty: number;
  unit: number;
  total: number;
  desc: string;
}

/**
 * Text (pdfItemsToPageText output, pages joined with \n) → SupplierInvoice.
 * Throws when the PDF is not a Tomer Gal / BWC Import invoice.
 */
export function parseTomerGalInvoiceText(text: string): SupplierInvoice {
  if (!RE_SUPPLIER.test(text)) {
    throw new Error('This PDF is not a Tomer Gal invoice (issuer B.W.C. Import / BWC IMPORT not found).');
  }
  const rows = text.split('\n').map(r => r.trim()).filter(Boolean);

  // -- Meta ------------------------------------------------------------------
  const num = /(\d+\/\d+)\s*:\s*מספר(?!כם)|מספר(?!כם)\s*:\s*(\d+\/\d+)/.exec(text);
  const date = /(\d{1,2})\/(\d{1,2})\/(\d{4})\s*:\s*תאריך|תאריך\s*:\s*(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(text);
  const invoiceNumber = num ? (num[1] ?? num[2] ?? '') : '';
  const invoiceDate = date
    ? [date[1] ?? date[4], date[2] ?? date[5], date[3] ?? date[6]]
        .map((p, i) => (i < 2 ? p!.padStart(2, '0') : p)).join('.')
    : '';

  // -- Table lines -----------------------------------------------------------
  const raw: RawLine[] = [];
  let inTable = false;
  for (const row of rows) {
    if (RE_TABLE_HEADER.test(row)) { inTable = true; continue; }
    if (!inTable) continue;
    if (RE_TOTAL_INCL.test(row)) { inTable = false; continue; }   // end of table (this page)
    const m = LINE_RE.exec(row);
    if (m) {
      raw.push({
        total: amount(m[1]!), unit: amount(m[2]!), qty: amount(m[3]!),
        desc: m[4]!.trim(), item: m[5]!, no: parseInt(m[6]!, 10),
      });
      continue;
    }
    // A wrapped description: no amounts, not page furniture.
    const prev = raw[raw.length - 1];
    if (prev && amountsIn(row).length === 0 && !RE_BOILERPLATE.test(row)) {
      prev.desc = `${prev.desc} ${row}`;
    }
  }

  // -- Totals ----------------------------------------------------------------
  const rowWith = (rx: RegExp) => rows.find(r => rx.test(r));
  const inclRow = rowWith(RE_TOTAL_INCL);
  const inclAmounts = inclRow ? amountsIn(inclRow) : [];
  const totalInclVat = inclAmounts.length ? inclAmounts[0]! : null;
  const totalQty = inclAmounts.length > 1 ? inclAmounts[inclAmounts.length - 1]! : null;
  const toPayRow = rowWith(RE_TOTAL_TO_PAY);
  const totalToPay = toPayRow ? (amountsIn(toPayRow)[0] ?? null) : null;
  const netRow = rowWith(RE_NET);
  const net = netRow ? (amountsIn(netRow)[0] ?? null) : null;
  const vatRow = rows.find(r => RE_VAT_ROW.test(r) && amountsIn(r).length > 0);
  const vatM = vatRow ? RE_VAT_ROW.exec(vatRow) : null;
  const vatRate = vatM ? parseFloat(vatM[1] ?? vatM[2]!) / 100 : null;
  const vatAmount = vatRow ? (amountsIn(vatRow)[0] ?? null) : null;

  // Invoice-level discount (if any) applied pro-rata: what was paid / list total.
  const payFactor = totalInclVat && totalToPay != null && totalInclVat > 0 ? totalToPay / totalInclVat : 1;

  // -- Classify lines --------------------------------------------------------
  const lines: ImportLine[] = [];
  const excluded: ExcludedLine[] = [];
  for (const r of raw) {
    const ref = `#${r.no}`;
    if (!HAS_LATIN.test(r.desc)) {
      excluded.push({ ref, description: r.desc, amount: r.total, kind: 'not_wine',
        reason: 'Not a wine (Hebrew-only description, e.g. tasting or delivery)' });
      continue;
    }
    const w = parseTomerGalWine(r.desc);
    const qtyInt = Math.round(r.qty);
    if (!w || qtyInt <= 0 || Math.abs(qtyInt - r.qty) > 1e-9) {
      excluded.push({ ref, description: r.desc, amount: r.total, kind: 'unparsed',
        reason: !w
          ? 'Could not read producer / vintage (expected "Producer - Wine 2022")'
          : `Quantity ${r.qty} is not a whole number of bottles` });
      continue;
    }
    const style = inferStyle(w.cuvee, w.producer);
    lines.push({
      key: `${r.no}`,
      ref,
      qty: qtyInt,
      linePrice: r.total,
      costPerBottle: round2((r.total * payFactor) / qtyInt),
      producerRaw: w.producer,
      producerName: w.producer,
      year: w.year,
      cuvee: w.cuvee,
      inferredStyle: style,
      inferredAppellation: inferAppellation(w.cuvee, { producerName: w.producer, style }),
      inOwc: false,
      format: w.format,
      bottleNote: `Tomer Gal invoice ${invoiceNumber || '?'} — line ${r.no}, item ${r.item}`
        + ` — ${r.unit.toFixed(2)} ILS/btl incl. VAT`,
    });
  }

  // -- Reconciliation --------------------------------------------------------
  // Two independent proofs that no line was dropped: the invoice numbers its
  // lines 1..N, and it prints the total quantity and total amount.
  const checks: ReconCheck[] = [];
  const nos = raw.map(r => r.no);
  const maxNo = nos.length ? Math.max(...nos) : 0;
  checks.push(mk('line_numbers', 'Invoice lines read (numbered 1…N, no gaps)',
    maxNo, new Set(nos).size, 'error', 'count', 0,
    'Lines are numbered on the invoice. A gap means a line was not read.'));
  checks.push(mk('qty', 'Σ quantity vs invoice total quantity',
    totalQty, raw.reduce((s, r) => s + r.qty, 0), 'error', 'count', 0.001));
  checks.push(mk('lines_total', 'Σ line totals vs "סה"כ כולל מע"מ"',
    totalInclVat, raw.reduce((s, r) => s + r.total, 0), 'error', 'money', 0.02,
    'Independent proof that every line was captured with the right amount.'));
  checks.push(mk('unit_x_qty', 'Σ (qty × unit price) vs Σ line totals',
    raw.reduce((s, r) => s + r.total, 0), raw.reduce((s, r) => s + r.qty * r.unit, 0), 'warning', 'money',
    Math.max(0.02, raw.length * 0.01),
    'A mismatch means a line carries its own discount, or a column was misread.'));
  if (net != null && vatAmount != null) {
    checks.push(mk('net_vat', 'Net + VAT vs "סה"כ לתשלום"', totalToPay, net + vatAmount, 'warning', 'money', 0.02));
  }
  if (net != null && vatRate != null) {
    checks.push(mk('vat', `${(vatRate * 100).toFixed(0)}% VAT on net`, vatAmount, net * vatRate, 'warning', 'money', 0.02));
  }
  const landed = lines.reduce((s, l) => s + l.costPerBottle * l.qty, 0)
    + excluded.reduce((s, e) => s + e.amount * payFactor, 0);
  checks.push(mk('landed', 'Σ bottle cost + excluded lines vs "סה"כ לתשלום"',
    totalToPay, landed, 'warning', 'money', Math.max(0.02, lines.reduce((s, l) => s + l.qty, 0) * 0.01),
    'Rounding each bottle to the agora can drift by up to 0.01 per bottle.'));
  const unparsed = excluded.filter(e => e.kind === 'unparsed').length;
  checks.push(mk('unparsed', 'Wine lines that could not be read', 0, unparsed, 'error', 'count', 0,
    'Fix the line by hand after import (Add Bottle), or check the PDF.'));

  return {
    lines,
    excluded,
    currency: 'ILS',
    invoiceNumber,
    invoiceDate,
    purchaseLotNote: `Tomer Gal (B.W.C. Import) invoice ${invoiceNumber}`
      + (vatRate != null ? ` — prices incl. ${(vatRate * 100).toFixed(0)}% VAT` : ''),
    reconciliation: {
      checks,
      hasErrors: checks.some(c => !c.ok && c.severity === 'error'),
      hasWarnings: checks.some(c => !c.ok && c.severity === 'warning'),
    },
  };
}
